/**
 * BeezFy Unified Variant Wizard
 * Sistema centralizzato per la selezione delle varianti
 */

(function() {
  'use strict';

  class BeezfyVariantWizard {
    constructor() {
      this.modal = null;
      this.product = null;
      this.selections = {};
      this.currentStep = 0;
      this.settings = window.BeezfyTheme?.settings || {};
      this.translations = window.beezfyTranslations || {};
      this.bundleColorImages = {};
      this.bundleOptionMetadata = {};
      this.aliasCache = {};
      this.componentProductCache = new Map();
      this.componentProductData = new Map();

      this.init();
    }

    init() {
      this.createModal();
      this.setupTriggerListeners();

      // Make globally accessible
      window.variantWizard = this;
    }

    setupTriggerListeners() {
      // Listen for clicks on variant wizard triggers
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-variant-wizard-trigger]');
        if (!trigger) return;

        e.preventDefault();
        e.stopPropagation();

        const productHandle = trigger.dataset.productHandle;
        const productId = trigger.dataset.productId;

        // Fetch product data and open wizard
        this.openForProduct(productHandle || productId);
      });
    }

    createModal() {
      // Check if modal already exists
      if (document.getElementById('beezfy-variant-wizard')) {
        this.modal = document.getElementById('beezfy-variant-wizard');
        this.initializeElements();
        return;
      }

      const modalHTML = `
        <div id="beezfy-variant-wizard" class="bz-vw-modal" role="dialog" aria-modal="true" aria-hidden="true">
          <div class="bz-vw-modal__overlay"></div>
          <div class="bz-vw-modal__container">
            <button class="bz-vw-modal__close" aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="2"/>
              </svg>
            </button>

            <div class="bz-vw-modal__header">
              <div class="bz-vw-modal__product-info">
                <img class="bz-vw-modal__product-image" src="" alt="">
                <div class="bz-vw-modal__product-details">
                  <h2 class="bz-vw-modal__product-title"></h2>
                  <div class="bz-vw-modal__product-price"></div>
                </div>
              </div>
            </div>

            <div class="bz-vw-modal__step-header">
              <div class="bz-vw-modal__step-title"></div>
            </div>

            <div class="bz-vw-modal__content">
              <div class="bz-vw-modal__options"></div>
            </div>

            <div class="bz-vw-modal__footer">
              <button class="bz-vw-modal__prev" disabled>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M12 5l-5 5 5 5" stroke="currentColor" stroke-width="2"/>
                </svg>
                <span>${this.translations.previous || 'Previous'}</span>
              </button>
              <button class="bz-vw-modal__next">
                <span>${this.translations.next || 'Next'}</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M8 5l5 5-5 5" stroke="currentColor" stroke-width="2"/>
                </svg>
              </button>
            </div>

            <div class="bz-vw-modal__steps-indicator"></div>
          </div>
        </div>
      `;

      document.body.insertAdjacentHTML('beforeend', modalHTML);
      this.modal = document.getElementById('beezfy-variant-wizard');

      // Initialize elements and events
      this.initializeElements();
    }

    initializeElements() {
      if (!this.modal) {
        return;
      }

      // Cache elements
      this.elements = {
        overlay: this.modal.querySelector('.bz-vw-modal__overlay'),
        closeBtn: this.modal.querySelector('.bz-vw-modal__close'),
        productImage: this.modal.querySelector('.bz-vw-modal__product-image'),
        productTitle: this.modal.querySelector('.bz-vw-modal__product-title'),
        productPrice: this.modal.querySelector('.bz-vw-modal__product-price'),
        stepsIndicator: this.modal.querySelector('.bz-vw-modal__steps-indicator'),
        stepTitle: this.modal.querySelector('.bz-vw-modal__step-title'),
        optionsContainer: this.modal.querySelector('.bz-vw-modal__options'),
        prevBtn: this.modal.querySelector('.bz-vw-modal__prev'),
        nextBtn: this.modal.querySelector('.bz-vw-modal__next'),
        content: this.modal.querySelector('.bz-vw-modal__content')
      };

      // Bind events
      this.bindEvents();
    }

    bindEvents() {
      if (this.elements.closeBtn) {
        this.elements.closeBtn.addEventListener('click', () => this.close());
      }
      if (this.elements.overlay) {
        this.elements.overlay.addEventListener('click', () => this.close());
      }
      if (this.elements.prevBtn) {
        this.elements.prevBtn.addEventListener('click', () => this.previousStep());
      }
      if (this.elements.nextBtn) {
        this.elements.nextBtn.addEventListener('click', () => {
          if (this.product) this.nextStep();
        });
      }

      // Keyboard navigation
      document.addEventListener('keydown', (e) => {
        if (!this.modal.classList.contains('active')) return;

        if (e.key === 'Escape') this.close();
        if (e.key === 'ArrowLeft' && this.product) this.previousStep();
        if (e.key === 'ArrowRight' && this.product && this.elements.nextBtn && !this.elements.nextBtn.disabled) this.nextStep();
      });
    }

    async openForProduct(productHandleOrId) {
      try {
        // Fetch product data
        const response = await fetch(`/products/${productHandleOrId}.js`);
        if (!response.ok) throw new Error('Product not found');

        const product = await response.json();

        // Preload bundle metadata if available
        await this.preloadBundleMetadata(product);

        this.open(product);
      } catch (error) {
        console.error('Failed to load product:', error);
      }
    }

    async open(product, selectedOptions = {}, onSelect = null) {

      // Ensure modal exists
      if (!this.modal) {
        console.error('Modal not initialized, creating now...');
        this.createModal();
        this.initializeElements();
        this.setupEventListeners();
      }

      this.product = product;
      this.selections = selectedOptions || {};
      Object.keys(this.selections).forEach((key) => {
        this.selections[key] = this.resolveOptionValue(this.selections[key]);
      });
      this.currentStep = 0;
      this.onSelectCallback = onSelect; // Store callback for when variant is selected
      this.bundleColorImages = {};
      this.bundleOptionMetadata = {};

      // Check if product has options
      if (!product.options || product.options.length === 0) {
        // If product has only one variant, add to cart directly
        if (product.variants && product.variants.length === 1) {
          if (this.onSelectCallback) {
            this.onSelectCallback(product.variants[0]);
          } else {
            this.addToCart(product.variants[0]);
          }
        }
        return;
      }

      // Enhanced bundle swatch preparation with card context
      await this.prepareBundleSwatches();

      // Check if we have bundle data from product cards
      if (!Object.keys(this.bundleColorImages).length) {
        await this.loadBundleDataFromCards();
      }

      // Update product info
      this.updateProductInfo();

      // Initialize steps
      this.initializeSteps();

      // Render first step
      this.renderStep();

      // Show modal
      if (this.modal) {
        this.modal.classList.add('active');
        document.body.classList.add('modal-open', 'overflow-hidden');
      } else {
        console.error('Modal element not found!');
      }
    }

    close() {
      this.modal.classList.remove('active');
      document.body.classList.remove('modal-open', 'overflow-hidden');
      if (this.elements?.content) {
        this.elements.content.classList.remove('bz-vw-modal__content--summary');
      }
    }

    updateProductInfo() {
      // Check if elements exist before updating
      if (!this.elements) {
        return;
      }

      // Update product image
      if (this.product.featured_image && this.elements.productImage) {
        this.elements.productImage.src = this.product.featured_image;
      }

      // Update title
      if (this.elements.productTitle) {
        this.elements.productTitle.textContent = this.product.title;
      }

      // Update price
      if (this.elements.productPrice) {
        if (this.product.price) {
          this.elements.productPrice.textContent = this.formatMoney(this.product.price);
        } else if (this.product.price_min && this.product.price_max) {
          if (this.product.price_min === this.product.price_max) {
            this.elements.productPrice.textContent = this.formatMoney(this.product.price_min);
          } else {
            this.elements.productPrice.textContent =
              `${this.formatMoney(this.product.price_min)} - ${this.formatMoney(this.product.price_max)}`;
          }
        }
      }
    }

    initializeSteps() {
      // Check if elements exist
      if (!this.elements || !this.elements.stepsIndicator) {
        return;
      }

      // Check if product has options
      if (!this.product.options || this.product.options.length === 0) {
        console.warn('Product has no options to display in wizard');
        return;
      }

      // Create step indicators
      const summaryIndex = this.product.options.length;
      const stepsHTML = this.product.options.map((option, index) => {
        const optionName = typeof option === 'string' ? option : (option.name || `Option ${index + 1}`);
        return `
          <div class="bz-vw-modal__step ${index === 0 ? 'active' : ''}" data-step="${index}">
            <span class="bz-vw-modal__step-number">${index + 1}</span>
            <span class="bz-vw-modal__step-name">${optionName}</span>
          </div>
        `;
      }).join('') + `
        <div class="bz-vw-modal__step" data-step="${summaryIndex}">
          <span class="bz-vw-modal__step-number">${summaryIndex + 1}</span>
          <span class="bz-vw-modal__step-name">${this.translations.wizardSummary || this.translations.summary || 'Review'}</span>
        </div>
      `;

      this.elements.stepsIndicator.innerHTML = stepsHTML;
    }

    renderStep() {
      const summaryIndex = this.getSummaryIndex();
      if (this.currentStep === summaryIndex) {
        if (this.elements?.content) {
          this.elements.content.classList.add('bz-vw-modal__content--summary');
        }
        this.renderSummaryStep();
        return;
      }

      if (this.elements?.content) {
        this.elements.content.classList.remove('bz-vw-modal__content--summary');
      }

      const option = this.product.options[this.currentStep];
      if (!option) {
        console.warn('No option found for step:', this.currentStep);
        return;
      }

      // Handle both string and object option formats
      const optionName = typeof option === 'string' ? option : (option.name || `Option ${this.currentStep + 1}`);

      // Get option values - if option is a string, extract values from variants
      let optionValues = [];
      if (typeof option === 'string') {
        // Extract unique values from variants for this option position
        const valueSet = new Set();
        if (Array.isArray(this.product.variants)) {
          this.product.variants.forEach(variant => {
            if (variant.options && variant.options[this.currentStep] !== undefined) {
              valueSet.add(variant.options[this.currentStep]);
            }
          });
        }
        optionValues = Array.from(valueSet);
      } else {
        optionValues = option.values || [];
      }

      // Update step title with translation
      const titleTemplate = this.translations.selectOption || 'Select {option_name}';
      this.elements.stepTitle.textContent = titleTemplate.replace('{option_name}', optionName);

      // Update step indicators
      this.modal.querySelectorAll('.bz-vw-modal__step').forEach((step) => {
        const stepIndex = parseInt(step.dataset.step, 10);
        step.classList.toggle('active', stepIndex === this.currentStep);
        step.classList.toggle('completed', stepIndex < this.currentStep);
      });

      // Create option object for rendering methods
      const optionPosition = typeof option.position === 'number' ? option.position : this.currentStep + 1;
      const optionForRender = {
        name: optionName,
        values: optionValues,
        position: optionPosition
      };

      // Render options based on swatch type
      const swatchType = this.getSwatchType(optionName);
      let optionsHTML = '';

      if (swatchType === 'color') {
        optionsHTML = this.renderColorSwatches(optionForRender);
      } else if (swatchType === 'image') {
        optionsHTML = this.renderImageSwatches(optionForRender);
      } else {
        optionsHTML = this.renderTextOptions(optionForRender);
      }

      this.elements.optionsContainer.innerHTML = optionsHTML;

      const containers = this.elements.optionsContainer.querySelectorAll('[data-swatches-container]');
      containers.forEach((container) => this.enrichWizardContainer(container));

      // Bind option click events
      this.bindOptionEvents();

      // Update buttons
      this.updateButtons();
    }

    renderSummaryStep() {
      const summaryIndex = this.getSummaryIndex();

      this.modal.querySelectorAll('.bz-vw-modal__step').forEach((step) => {
        const stepIndex = parseInt(step.dataset.step, 10);
        step.classList.toggle('active', stepIndex === summaryIndex);
        step.classList.toggle('completed', stepIndex < summaryIndex);
      });

      const title = this.translations.wizardSummaryTitle || this.translations.summaryTitle || this.translations.summary || 'Review your selection';
      this.elements.stepTitle.textContent = title;

      const selectedVariant = this.getSelectedVariant();
      const summaryItems = this.product.options.map((option, index) => {
        const optionName = typeof option === 'string' ? option : (option.name || `Option ${index + 1}`);
        const selection = this.getSelectionForOption(optionName) || '-';
        return `
          <li class="bz-vw-summary__item">
            <span class="bz-vw-summary__item-label">${optionName}</span>
            <span class="bz-vw-summary__item-value">${selection}</span>
          </li>
        `;
      }).join('');

      const summaryImages = [];
      const pushImage = (src) => {
        if (!src) return;
        if (!summaryImages.includes(src)) summaryImages.push(src);
      };
      if (selectedVariant?.featured_media?.preview_image?.src) {
        pushImage(selectedVariant.featured_media.preview_image.src);
      }
      if (selectedVariant?.featured_media?.src) {
        pushImage(selectedVariant.featured_media.src);
      }
      if (selectedVariant?.featured_image) {
        if (typeof selectedVariant.featured_image === 'string') {
          pushImage(selectedVariant.featured_image);
        } else if (selectedVariant.featured_image?.src) {
          pushImage(selectedVariant.featured_image.src);
        }
      }
      if (!summaryImages.length && this.product.featured_image) {
        pushImage(this.product.featured_image);
      }

      const mediaHtml = summaryImages.map((src, index) => {
        const safeSrc = this.escapeAttribute(src);
        const altText = this.escapeAttribute(`${this.product.title} ${index + 1}`);
        return `
          <div class="bz-vw-summary__image-wrap">
            <img class="bz-vw-summary__image" src="${safeSrc}" alt="${altText}">
          </div>
        `;
      }).join('');

      const availabilityMessage = !selectedVariant
        ? `<div class="bz-vw-summary__alert">${this.translations.wizardUnavailable || 'Selected combination is not available. Please adjust your choices.'}</div>`
        : selectedVariant.available
          ? ''
          : `<div class="bz-vw-summary__alert">${this.translations.wizardOutOfStock || 'Selected variant is out of stock. Please adjust your choices.'}</div>`;

      this.elements.optionsContainer.innerHTML = `
        <div class="bz-vw-summary">
          <div class="bz-vw-summary__body">
            ${summaryImages.length ? `
              <div class="bz-vw-summary__media">
                ${mediaHtml}
              </div>
            ` : ''}
            <ul class="bz-vw-summary__list">
              ${summaryItems}
            </ul>
          </div>
          ${availabilityMessage}
          <button type="button" class="bz-vw-summary__confirm" ${!selectedVariant || !selectedVariant.available ? 'disabled' : ''}>
            ${this.translations.wizardConfirm || this.translations.wizardAddToCart || this.translations.add_to_cart || 'Confirm & Add to Cart'}
          </button>
        </div>
      `;

      const confirmButton = this.elements.optionsContainer.querySelector('.bz-vw-summary__confirm');
      if (confirmButton) {
        confirmButton.addEventListener('click', () => this.addToCart());
      }

      this.updateButtons();
    }

    getSummaryIndex() {
      return Array.isArray(this.product?.options) ? this.product.options.length : 0;
    }

    getLastOptionIndex() {
      const summaryIndex = this.getSummaryIndex();
      return summaryIndex > 0 ? summaryIndex - 1 : -1;
    }

    getSelectionForOption(optionName) {
      if (!optionName) return undefined;
      if (this.selections && Object.prototype.hasOwnProperty.call(this.selections, optionName)) {
        return this.selections[optionName];
      }
      const normalizedSearch = this.normalizeLabel(optionName);
      const matchingKey = Object.keys(this.selections || {}).find(key => this.normalizeLabel(key) === normalizedSearch);
      if (matchingKey) {
        return this.selections[matchingKey];
      }
      return undefined;
    }

    getSelectedVariant() {
      if (!this.product) return null;

      const optionSequence = Array.isArray(this.product.options) ? this.product.options : [];
      if (!optionSequence.length) {
        return this.product.variants?.[0] || null;
      }

      const selectedOptions = optionSequence.map((option) => {
        const optionName = typeof option === 'string' ? option : option?.name;
        if (!optionName) return undefined;
        return this.getSelectionForOption(optionName);
      });

      if (!selectedOptions.every(value => value !== undefined && value !== null)) {
        return null;
      }

      const variant = this.product.variants?.find(v => {
        return v.options && v.options.every((opt, index) => opt === selectedOptions[index]);
      });

      return variant || null;
    }

    getSwatchType(optionName) {
      if (!optionName || typeof optionName !== 'string') {
        console.warn('getSwatchType: optionName is invalid:', optionName);
        return 'text';
      }

      const type = this.determineOptionType(optionName);
      if (type === 'color' || type === 'image') {
        return type;
      }
      return 'text';
    }

    renderColorSwatches(option) {
      if (!option || !option.values || !Array.isArray(option.values)) {
        console.warn('renderColorSwatches: invalid option data:', option);
        return '<div class="bz-vw-modal__swatches">No color options available</div>';
      }

      // Check if we should show variant images for colors
      const showImages = this.settings.wizard?.show_variant_images !== false;
      const context = this.getBundleOptionContext(option.name);
      const optionPosition = typeof option.position === 'number' ? option.position : this.currentStep + 1;
      const normalizedOptionKey = this.normalizeLabel(option.name);
      const optionAttrs = [
        'class="bz-vw-modal__swatches bz-vw-modal__swatches--color"',
        `data-option-name="${this.escapeAttribute(option.name)}"`,
        'data-option-type="color"',
        'data-swatches-container',
        'data-swatch-context="wizard"',
        'data-swatch-mode="color"'
      ];

      if (this.product?.id) {
        optionAttrs.push(`data-product-id="${this.escapeAttribute(this.product.id)}"`);
      }
      if (this.product?.handle) {
        optionAttrs.push(`data-product-handle="${this.escapeAttribute(this.product.handle)}"`);
      }
      optionAttrs.push(`data-option-position="${optionPosition}"`);
      const bundleMap = this.bundleColorImages[normalizedOptionKey] || {};
      this.bundleColorImages[normalizedOptionKey] = bundleMap;
      if (context.isBundleOption) {
        optionAttrs.push('data-is-bundle-option="true"');
        optionAttrs.push('data-is-bundle="true"');
        if (context.componentHandle) {
          optionAttrs.push(`data-bundle-component="${this.escapeAttribute(context.componentHandle)}"`);
        }
        if (context.componentTitle) {
          optionAttrs.push(`data-bundle-component-title="${this.escapeAttribute(context.componentTitle)}"`);
        }
      }

      // Load unavailable settings
      const unavailableMode = this.settings.beezfy_swatch_unavailable_mode || 'show';
      const showUnavailableTooltip = this.settings.beezfy_swatch_show_unavailable_tooltip !== false;

      return `
        <div ${optionAttrs.join(' ')}>
          ${option.values.map(value => {
          const resolvedValue = this.resolveOptionValue(value);
          const colorCode = this.getColorCode(resolvedValue);
          const isSelected = this.selections[option.name] === resolvedValue;

          // Check if this option value is available given previous selections
          const optionIndex = optionPosition - 1;
          const isAvailable = this.isOptionValueAvailableInWizard(optionIndex, resolvedValue);

          // Skip rendering if mode is 'hide' and unavailable
          if (!isAvailable && unavailableMode === 'hide') {
            return '';
          }

          const media = bundleMap[this.normalizeLabel(resolvedValue)] || {};
          let swatchImage = showImages ? (media.image || media.thumbnail || null) : null;
          if (showImages && !swatchImage && context.componentHandle) {
            const componentMedia = this.getComponentImageFromCache(context.componentHandle, resolvedValue, context);
            if (componentMedia?.image) {
              swatchImage = componentMedia.image;
              bundleMap[this.normalizeLabel(resolvedValue)] = componentMedia;
            }
          }
          if (showImages && !swatchImage) {
            swatchImage = this.getSwatchImage(option.name, resolvedValue) || this.getVariantImage(option.name, resolvedValue);
          }
          const swatchImageEsc = this.escapeAttribute(swatchImage || '');
          // Always show labels in wizard
          const showLabel = true;

          const unavailableClass = !isAvailable ? ' bz-vw-modal__swatch--unavailable' : '';
          const buttonAttrs = [
            'class="bz-vw-modal__swatch bz-vw-modal__swatch--color ' + (isSelected ? 'selected' : '') + unavailableClass + '"',
            `data-option="${this.escapeAttribute(option.name)}"`,
            `data-value="${this.escapeAttribute(resolvedValue)}"`,
            `data-option-value="${this.escapeAttribute(resolvedValue)}"`,
            `data-option-position="${optionPosition}"`,
            `title="${this.escapeAttribute(resolvedValue)}"`,
            `aria-label="${this.escapeAttribute(resolvedValue)}"`,
            `aria-pressed="${isSelected ? 'true' : 'false'}"`
          ];

          if (!isAvailable) {
            buttonAttrs.push(`data-unavailable-mode="${unavailableMode}"`);
          }

          if (context.isBundleOption) {
            buttonAttrs.push('data-is-bundle-option="true"');
            buttonAttrs.push('data-is-bundle="true"');
            if (context.componentHandle) {
              buttonAttrs.push(`data-bundle-component="${this.escapeAttribute(context.componentHandle)}"`);
            }
            if (context.componentTitle) {
              buttonAttrs.push(`data-bundle-component-title="${this.escapeAttribute(context.componentTitle)}"`);
            }
          }

          const swatchUnavailableClass = !isAvailable ? ' beezfy-swatch--unavailable beezfy-swatch--disabled' : '';
          const swatchAttrs = [
            'class="beezfy-swatch beezfy-swatch--wizard beezfy-swatch--color' + swatchUnavailableClass + '"',
            'data-swatch',
            `data-option-name="${this.escapeAttribute(option.name)}"`,
            `data-option-value="${this.escapeAttribute(resolvedValue)}"`,
            `data-option-position="${optionPosition}"`,
            `data-swatch-label="${this.escapeAttribute(resolvedValue)}"`,
            `data-swatch-image="${swatchImageEsc}"`,
            `data-option-type="color"`,
            `aria-pressed="${isSelected ? 'true' : 'false'}"`
          ];

          if (!isAvailable) {
            swatchAttrs.push(`data-unavailable-mode="${unavailableMode}"`);
          }

          if (context.isBundleOption) {
            swatchAttrs.push('data-is-bundle-option="true"');
            swatchAttrs.push('data-is-bundle="true"');
            if (context.componentHandle) {
              swatchAttrs.push(`data-bundle-component="${this.escapeAttribute(context.componentHandle)}"`);
            }
            if (context.componentTitle) {
              swatchAttrs.push(`data-bundle-component-title="${this.escapeAttribute(context.componentTitle)}"`);
            }
          }

          const unavailableMarkup = !isAvailable ? `
            ${unavailableMode === 'show' ? `
              <span class="beezfy-swatch__unavailable">
                <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="position: absolute; top: 0; left: 0; pointer-events: none;">
                  <line x1="2" y1="2" x2="22" y2="22" stroke="rgba(220, 38, 38, 0.9)" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </span>
            ` : ''}
            ${showUnavailableTooltip ? `
              <span class="beezfy-swatch__tooltip--unavailable">
                ${this.translations.sold_out || 'Sold out'}
              </span>
            ` : ''}
          ` : '';

          return `
            <button ${buttonAttrs.join(' ')}>
              <span ${swatchAttrs.join(' ')}>
                ${swatchImage ?
                  `<img src="${swatchImageEsc}" alt="${this.escapeAttribute(resolvedValue)}" class="beezfy-swatch__image" loading="lazy">` :
                  `<span class="bz-vw-modal__swatch-color"
                        style="background-color: ${colorCode};
                               background-image: ${colorCode.includes('gradient') ? colorCode : 'none'};">
                  </span>`
                }
                ${unavailableMarkup}
              </span>
              ${showLabel ? `<span class="bz-vw-modal__swatch-label">${this.escapeAttribute(resolvedValue)}</span>` : ''}
            </button>
          `;
          }).join('')}
        </div>
      `;
    }

    renderImageSwatches(option) {
      if (!option || !option.values || !Array.isArray(option.values)) {
        console.warn('renderImageSwatches: invalid option data:', option);
        return '<div class="bz-vw-modal__swatches">No image options available</div>';
      }

      const context = this.getBundleOptionContext(option.name);
      const optionPosition = typeof option.position === 'number' ? option.position : this.currentStep + 1;
      const normalizedOptionKey = this.normalizeLabel(option.name);
      const bundleMap = this.bundleColorImages[normalizedOptionKey] || {};
      this.bundleColorImages[normalizedOptionKey] = bundleMap;

      const containerAttrs = [
        'class="bz-vw-modal__swatches bz-vw-modal__swatches--image"',
        'data-swatches-container',
        'data-swatch-context="wizard"',
        `data-option-name="${this.escapeAttribute(option.name)}"`,
        'data-option-type="image"',
        'data-swatch-mode="image"'
      ];

      containerAttrs.push(`data-option-position="${optionPosition}"`);

      if (this.product?.id) {
        containerAttrs.push(`data-product-id="${this.escapeAttribute(this.product.id)}"`);
      }
      if (this.product?.handle) {
        containerAttrs.push(`data-product-handle="${this.escapeAttribute(this.product.handle)}"`);
      }
      if (context.isBundleOption) {
        containerAttrs.push('data-is-bundle-option="true"');
        containerAttrs.push('data-is-bundle="true"');
        if (context.componentHandle) {
          containerAttrs.push(`data-bundle-component="${this.escapeAttribute(context.componentHandle)}"`);
        }
        if (context.componentTitle) {
          containerAttrs.push(`data-bundle-component-title="${this.escapeAttribute(context.componentTitle)}"`);
        }
      }

      // Load unavailable settings
      const unavailableMode = this.settings.beezfy_swatch_unavailable_mode || 'show';
      const showUnavailableTooltip = this.settings.beezfy_swatch_show_unavailable_tooltip !== false;

      return `
        <div ${containerAttrs.join(' ')}>
          ${option.values.map(value => {
            const resolvedValue = this.resolveOptionValue(value);

            // Check if this option value is available given previous selections
            const optionIndex = optionPosition - 1;
            const isAvailable = this.isOptionValueAvailableInWizard(optionIndex, resolvedValue);

            // Skip rendering if mode is 'hide' and unavailable
            if (!isAvailable && unavailableMode === 'hide') {
              return '';
            }

            const media = bundleMap[this.normalizeLabel(resolvedValue)] || {};
            let imageUrl = media.image || media.thumbnail || null;
            if (!imageUrl && context.componentHandle) {
              const componentMedia = this.getComponentImageFromCache(context.componentHandle, resolvedValue, context);
              if (componentMedia?.image) {
                imageUrl = componentMedia.image;
                bundleMap[this.normalizeLabel(resolvedValue)] = componentMedia;
              }
            }
            if (!imageUrl) {
              imageUrl = this.getSwatchImage(option.name, resolvedValue) || this.getVariantImage(option.name, resolvedValue);
            }

            const imageUrlEsc = this.escapeAttribute(imageUrl || '');
            const isSelected = this.selections[option.name] === resolvedValue;
            // Always show labels in wizard
            const showLabel = true;

            const unavailableClass = !isAvailable ? ' bz-vw-modal__swatch--unavailable' : '';
            const buttonAttrs = [
              'class="bz-vw-modal__swatch bz-vw-modal__swatch--image ' + (isSelected ? 'selected' : '') + unavailableClass + '"',
              `data-option="${this.escapeAttribute(option.name)}"`,
              `data-value="${this.escapeAttribute(resolvedValue)}"`,
              `data-option-value="${this.escapeAttribute(resolvedValue)}"`,
              `data-option-position="${optionPosition}"`,
              `title="${this.escapeAttribute(resolvedValue)}"`,
              `aria-label="${this.escapeAttribute(resolvedValue)}"`,
              `aria-pressed="${isSelected ? 'true' : 'false'}"`
            ];

            if (!isAvailable) {
              buttonAttrs.push(`data-unavailable-mode="${unavailableMode}"`);
            }

            if (context.isBundleOption) {
              buttonAttrs.push('data-is-bundle-option="true"');
              buttonAttrs.push('data-is-bundle="true"');
              if (context.componentHandle) {
                buttonAttrs.push(`data-bundle-component="${this.escapeAttribute(context.componentHandle)}"`);
              }
              if (context.componentTitle) {
                buttonAttrs.push(`data-bundle-component-title="${this.escapeAttribute(context.componentTitle)}"`);
              }
            }

            const swatchUnavailableClass = !isAvailable ? ' beezfy-swatch--unavailable beezfy-swatch--disabled' : '';
            const swatchAttrs = [
              'class="beezfy-swatch beezfy-swatch--wizard beezfy-swatch--image' + swatchUnavailableClass + '"',
              'data-swatch',
              `data-option-name="${this.escapeAttribute(option.name)}"`,
              `data-option-value="${this.escapeAttribute(resolvedValue)}"`,
              `data-option-position="${optionPosition}"`,
              `data-swatch-label="${this.escapeAttribute(resolvedValue)}"`,
              `data-swatch-image="${imageUrlEsc}"`,
              `data-option-type="image"`,
              `aria-pressed="${isSelected ? 'true' : 'false'}"`
            ];

            if (!isAvailable) {
              swatchAttrs.push(`data-unavailable-mode="${unavailableMode}"`);
            }

            if (context.isBundleOption) {
              swatchAttrs.push('data-is-bundle-option="true"');
              swatchAttrs.push('data-is-bundle="true"');
              if (context.componentHandle) {
                swatchAttrs.push(`data-bundle-component="${this.escapeAttribute(context.componentHandle)}"`);
              }
              if (context.componentTitle) {
                swatchAttrs.push(`data-bundle-component-title="${this.escapeAttribute(context.componentTitle)}"`);
              }
            }

            const unavailableMarkup = !isAvailable ? `
              ${unavailableMode === 'show' ? `
                <span class="beezfy-swatch__unavailable">
                  <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="position: absolute; top: 0; left: 0; pointer-events: none;">
                    <line x1="2" y1="2" x2="22" y2="22" stroke="rgba(220, 38, 38, 0.9)" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </span>
              ` : ''}
              ${showUnavailableTooltip ? `
                <span class="beezfy-swatch__tooltip--unavailable">
                  ${this.translations.sold_out || 'Sold out'}
                </span>
              ` : ''}
            ` : '';

            return `
              <button ${buttonAttrs.join(' ')}>
                <span ${swatchAttrs.join(' ')}>
                  ${imageUrl ?
                    `<img src="${imageUrlEsc}" alt="${this.escapeAttribute(resolvedValue)}" class="beezfy-swatch__image" loading="lazy">` :
                    `<span class="bz-vw-modal__swatch-text">${this.escapeAttribute(resolvedValue)}</span>`
                  }
                  ${unavailableMarkup}
                </span>
                ${showLabel ? `<span class="bz-vw-modal__swatch-label">${this.escapeAttribute(resolvedValue)}</span>` : ''}
              </button>
            `;
          }).join('')}
        </div>
      `;
    }

    renderTextOptions(option) {
      if (!option || !option.values || !Array.isArray(option.values)) {
        console.warn('renderTextOptions: invalid option data:', option);
        return '<div class="bz-vw-modal__options-list">No options available</div>';
      }

      return `
        <div class="bz-vw-modal__options-list">
          ${option.values.map(value => {
            const resolvedValue = this.resolveOptionValue(value);
            const isSelected = this.selections[option.name] === resolvedValue;

            return `
              <button class="bz-vw-modal__option ${isSelected ? 'selected' : ''}"
                      data-option="${this.escapeAttribute(option.name)}"
                      data-value="${this.escapeAttribute(resolvedValue)}"
                      aria-pressed="${isSelected ? 'true' : 'false'}">
                ${this.escapeAttribute(resolvedValue)}
              </button>
            `;
          }).join('')}
        </div>
      `;
    }

    bindOptionEvents() {
      const options = this.modal.querySelectorAll('[data-option]');

      options.forEach(option => {
        option.addEventListener('click', (e) => {
          const optionName = e.currentTarget.dataset.option;
          const optionValue = e.currentTarget.dataset.value;

          // BEEZFY PREMIUM: Prevent clicks on unavailable options
          const isUnavailable = e.currentTarget.classList.contains('bz-swatch--unavailable') ||
                               e.currentTarget.classList.contains('beezfy-swatch--disabled') ||
                               e.currentTarget.hasAttribute('disabled');

          if (isUnavailable) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }

          // Normal flow: Update selection
          this.selections[optionName] = optionValue;

          // Update UI
          options.forEach(opt => {
            if (opt.dataset.option === optionName) {
              opt.classList.toggle('selected', opt.dataset.value === optionValue);
              opt.setAttribute('aria-pressed', opt.dataset.value === optionValue ? 'true' : 'false');
            }
          });

          setTimeout(() => this.nextStep(), 300);
        });
      });
    }

    updateButtons() {
      const summaryIndex = this.getSummaryIndex();
      const lastOptionIndex = this.getLastOptionIndex();

      // Update prev button
      this.elements.prevBtn.disabled = this.currentStep === 0;

      if (this.currentStep === summaryIndex) {
        const selectedVariant = this.getSelectedVariant();
        const canConfirm = !!(selectedVariant && selectedVariant.available);
        this.elements.nextBtn.textContent =
          this.translations.wizardConfirm || this.translations.wizardAddToCart || this.translations.add_to_cart || 'Confirm & Add to Cart';
        this.elements.nextBtn.disabled = !canConfirm;
        this.elements.nextBtn.classList.toggle('bz-vw-modal__next--primary', canConfirm);
        return;
      }

      const option = this.product.options?.[this.currentStep];
      const optionName = typeof option === 'string' ? option : option?.name;
      const hasSelection = !!this.getSelectionForOption(optionName);

      if (this.currentStep === lastOptionIndex) {
        this.elements.nextBtn.textContent =
          hasSelection
            ? (this.translations.wizardReview || this.translations.review || this.translations.next || 'Review')
            : (this.translations.select || 'Select');
        this.elements.nextBtn.disabled = !hasSelection;
        this.elements.nextBtn.classList.toggle('bz-vw-modal__next--primary', hasSelection);
        return;
      }

      this.elements.nextBtn.innerHTML = `
        ${this.translations.next || 'Next'}
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M8 5l5 5-5 5" stroke="currentColor" stroke-width="2"/>
        </svg>
      `;
      this.elements.nextBtn.disabled = !hasSelection;
      this.elements.nextBtn.classList.remove('bz-vw-modal__next--primary');
    }

    nextStep() {
      // Safety checks
      if (!this.product || !this.product.options) {
        console.error('Product or options not available');
        return;
      }

      const lastOptionIndex = this.getLastOptionIndex();
      const summaryIndex = this.getSummaryIndex();

      if (this.currentStep <= lastOptionIndex) {
        const option = this.product.options[this.currentStep];
        if (!option) {
          console.error('Option not found for step:', this.currentStep);
          return;
        }

        const optionName = typeof option === 'string' ? option : option?.name;
        const hasSelection = !!this.getSelectionForOption(optionName);
        if (!hasSelection) return;

        if (this.currentStep < lastOptionIndex) {
          this.currentStep++;
          this.renderStep();
          return;
        }

        // Move to summary step
        this.currentStep = summaryIndex;
        this.renderStep();
        return;
      }

      if (this.currentStep === summaryIndex) {
        this.addToCart();
      }
    }

    previousStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
        this.renderStep();
      }
    }

    async addToCart(variantOverride = null) {
      const variant = variantOverride || this.getSelectedVariant();

      if (!variant) {
        this.showError(this.translations.wizardUnavailable || 'Selected combination not available');
        return;
      }

      if (!variant.available) {
        this.showError(this.translations.wizardOutOfStock || 'Selected variant is out of stock');
        return;
      }

      // Close modal
      this.close();

      // If we have a callback, use it instead of adding to cart directly
      if (this.onSelectCallback) {
        this.onSelectCallback(variant);
        return;
      }

      // Add to cart using unified system or AJAX
      try {
        if (window.BeezfyATCSystem && typeof window.BeezfyATCSystem.addToCart === 'function') {
          await window.BeezfyATCSystem.addToCart(variant.id, 1, {
            source: 'variant-wizard'
          });
        } else {
          // Fallback: Use AJAX
          const formData = new FormData();
          formData.append('id', variant.id);
          formData.append('quantity', '1');

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          if (!response.ok) {
            throw new Error('Failed to add to cart');
          }

          // Get cart response and trigger update events
          const cartData = await response.json();

          // Dispatch events for cart system to handle
          console.warn('🚀 Variant Wizard: Dispatching cart events after add');
          document.dispatchEvent(new CustomEvent('cart:add', { detail: cartData }));
          document.dispatchEvent(new CustomEvent('product:added', { detail: cartData }));

          // Update cart with new premium cart system
          if (window.BeezfyCart) {
            console.warn('🚀 Variant Wizard: Using Premium Cart System');
            // New premium cart system
            window.BeezfyCart.refresh();
            // DISABLED: Don't auto-open drawer - popup will show instead
            // setTimeout(() => {
            //   window.BeezfyCart.open();
            // }, 300);
          } else if (window.bfyCart) {
            console.warn('🚀 Variant Wizard: Using legacy cart system');
            // Legacy cart fallback
            if (window.bfyCart.refreshCart) {
              window.bfyCart.refreshCart().then(() => {
                if (typeof window.bfyCart.open === 'function') {
                  window.bfyCart.open();
                }
              });
            }
          }
        }

        // Show success popup
        setTimeout(() => {
          if (window.BeezfyPopup) {
            // Get product data for popup
            const product = this.product;
            window.BeezfyPopup.show({
              product: product,
              variant: variant,
              quantity: 1
            });
          }
        }, 100);

      } catch (error) {
        console.error('Failed to add to cart:', error);
        this.showError('Failed to add to cart');
      }
    }

    showError(message) {
      const error = document.createElement('div');
      error.className = 'bz-vw-modal__error';
      error.textContent = message;

      this.elements.optionsContainer.appendChild(error);

      setTimeout(() => error.remove(), 3000);
    }

    getColorCode(colorName) {
      const lowerName = colorName.toLowerCase().replace(/\s+/g, '');

      // First check variant wizard specific color mappings
      if (this.settings.wizard_color_mappings) {
        const wizardColors = JSON.parse(this.settings.wizard_color_mappings || '{}');
        if (wizardColors[lowerName]) {
          return wizardColors[lowerName];
        }
      }

      // Check global swatch system colors
      const globalColors = window.BeezfyTheme?.swatches?.colors || {};
      if (globalColors[lowerName]) {
        return globalColors[lowerName];
      }

      // Check theme metafields for color definitions
      if (window.Shopify?.theme?.colorSwatches) {
        const themeColor = window.Shopify.theme.colorSwatches[lowerName];
        if (themeColor) return themeColor;
      }

      // Extended default color mappings
      const colors = {
        'black': '#000000',
        'white': '#FFFFFF',
        'red': '#FF0000',
        'blue': '#0000FF',
        'green': '#00FF00',
        'yellow': '#FFFF00',
        'pink': '#FFC0CB',
        'purple': '#800080',
        'orange': '#FFA500',
        'brown': '#A52A2A',
        'grey': '#808080',
        'gray': '#808080',
        'navy': '#000080',
        'navyblue': '#000080',
        'beige': '#F5F5DC',
        'gold': '#FFD700',
        'silver': '#C0C0C0',
        'maroon': '#800000',
        'olive': '#808000',
        'lime': '#00FF00',
        'aqua': '#00FFFF',
        'teal': '#008080',
        'fuchsia': '#FF00FF',
        'crimson': '#DC143C',
        'coral': '#FF7F50',
        'salmon': '#FA8072',
        'khaki': '#F0E68C',
        'lavender': '#E6E6FA',
        'violet': '#EE82EE',
        'plum': '#DDA0DD',
        'mint': '#98FF98',
        'ivory': '#FFFFF0',
        'pearl': '#F8F8FF'
      };

      return colors[lowerName] || '#E5E7EB';
    }

    getVariantImage(optionName, value) {
      const normalizedOption = this.normalizeLabel(optionName);
      const normalizedValue = this.normalizeLabel(value);

      if (normalizedOption && normalizedValue && this.bundleColorImages[normalizedOption]?.[normalizedValue]) {
        const bundleMedia = this.bundleColorImages[normalizedOption][normalizedValue];
        if (bundleMedia?.thumbnail || bundleMedia?.image) {
          return bundleMedia.thumbnail || bundleMedia.image;
        }
      }

      // Find variant that matches this option value
      const optionIndex = Array.isArray(this.product.options)
        ? this.product.options.findIndex(opt => {
            if (typeof opt === 'string') return opt === optionName;
            if (opt && typeof opt === 'object' && opt.name) return opt.name === optionName;
            return false;
          })
        : -1;

      if (optionIndex === -1) return null;

      // Find first variant with this option value
      const variant = this.product.variants.find(v => {
        // Shopify stores option values as option1, option2, option3
        const optionKey = `option${optionIndex + 1}`;
        return v[optionKey] === value;
      });

      // Return variant featured image if available
      if (variant?.featured_image) {
        return variant.featured_image.src || variant.featured_image;
      }

      return null;
    }

    async prepareBundleSwatches() {
      if (!window.BeezfySwatchDataStore || !this.product) return;

      try {
        const store = window.BeezfySwatchDataStore;
        const handle = this.product.handle || this.product.id;
        const optionsWithValues = this.product.options_with_values || this.product.options || [];

        for (const option of optionsWithValues) {
          const optionName = typeof option === 'string' ? option : option?.name;
          if (!optionName) continue;

          const optionType = this.determineOptionType(optionName);
          if (optionType !== 'color') continue;

          const optionValuesRaw = Array.isArray(option?.values) ? option.values.slice() : [];
          if (!optionValuesRaw.length) continue;

          const resolvedValues = Array.from(new Set(optionValuesRaw
            .map((value) => this.resolveOptionValue(value))
            .filter((value) => value !== '')));

          if (!resolvedValues.length) continue;

          const context = this.getBundleOptionContext(optionName);

          const key = this.normalizeLabel(optionName);
          let map = this.bundleColorImages[key] || {};

          const existingImages = this.collectExistingBundleImages(optionName, context);
          if (existingImages && Object.keys(existingImages).length) {
            map = Object.assign({}, map, existingImages);
          }

          if (context.componentHandle) {
            const componentImages = await this.loadComponentImages(context, resolvedValues);
            if (componentImages && Object.keys(componentImages).length) {
              map = Object.assign({}, map, componentImages);
            }
          }

          this.bundleColorImages[key] = map;

          const missingValues = resolvedValues.filter((value) => {
            const normalizedValue = this.normalizeLabel(value);
            const media = map[normalizedValue];
            return !media || !media.image;
          });

          if (!missingValues.length) {
            continue;
          }

          const valueMetadata = missingValues.map((value) => ({
            value,
            label: value,
            option: optionName,
            component: context.componentHandle || '',
            componentTitle: context.componentTitle || ''
          }));

          const imagesMap = await store.getColorImages({
            productHandle: handle,
            optionName,
            optionValues: missingValues,
            productData: this.product,
            bundleComponent: context.componentHandle || null,
            bundleComponentTitle: context.componentTitle || null,
            valueMetadata
          });

          if (imagesMap && Object.keys(imagesMap).length) {
            const current = this.bundleColorImages[key] || {};
            this.bundleColorImages[key] = Object.assign({}, current, imagesMap);
          }
        }
      } catch (error) {
        console.warn('[BeezfyVariantWizard] Failed to prepare bundle swatches', error);
      }
    }

    getBundleOptionContext(optionName) {
      const normalized = this.normalizeLabel(optionName);
      if (!normalized) {
        return {
          optionName: optionName || '',
          normalizedOption: '',
          attribute: '',
          componentTitle: '',
          componentHandle: '',
          isBundleOption: false
        };
      }

      if (this.bundleOptionMetadata[normalized]) {
        return this.bundleOptionMetadata[normalized];
      }

      const context = this.extractBundleOptionContext(optionName);
      this.bundleOptionMetadata[normalized] = context;
      return context;
    }

    extractBundleOptionContext(optionName) {
      const context = {
        optionName: optionName || '',
        normalizedOption: this.normalizeLabel(optionName),
        attribute: '',
        componentTitle: '',
        componentHandle: '',
        isBundleOption: false
      };

      if (!optionName) return context;

      const raw = optionName.toString();
      const parenMatch = raw.match(/^(.*)\(([^)]+)\)\s*$/);
      if (parenMatch) {
        const potentialTitle = parenMatch[1].trim();
        const attributeToken = parenMatch[2].trim();
        if (attributeToken) {
          context.attribute = attributeToken;
        }
        if (potentialTitle && potentialTitle.length && potentialTitle.toLowerCase() !== raw.toLowerCase()) {
          context.componentTitle = potentialTitle;
          context.componentHandle = this.handleize(potentialTitle);
          context.isBundleOption = true;
        }
      }

      if (!context.attribute && raw.includes(':')) {
        const parts = raw.split(':');
        const attribute = parts.pop();
        if (attribute) {
          context.attribute = attribute.trim();
        }
      }

      return context;
    }

    collectExistingBundleImages(optionName, context = {}) {
      const map = {};
      if (!optionName) return map;

      const variantSelector = document.querySelector('[data-variant-selector]');
      if (!variantSelector) return map;

      const containers = Array.from(variantSelector.querySelectorAll('[data-option-name]'))
        .filter((container) => container.dataset.optionName === optionName);

      containers.forEach((container) => {
        const swatches = container.querySelectorAll('[data-swatch]');
        swatches.forEach((swatch) => {
          const value = this.resolveOptionValue(swatch.dataset.optionValue || swatch.dataset.swatchLabel || '');
          const normalized = this.normalizeLabel(value);
          if (!normalized) return;

          const image = swatch.dataset.swatchImage || '';
          const thumbnail = swatch.dataset.swatchThumbnail || image;
          if (!image && !thumbnail) return;

          map[normalized] = {
            image: image || thumbnail || '',
            thumbnail: thumbnail || image || '',
            alt: swatch.dataset.swatchLabel || value,
            variantId: swatch.dataset.variantId || null,
            component: swatch.dataset.bundleComponent || context.componentHandle || '',
            componentTitle: swatch.dataset.bundleComponentTitle || context.componentTitle || '',
            source: 'pdp'
          };
        });
      });

      if (!Object.keys(map).length && this.product?.handle) {
        const handle = this.product.handle;
        const escapedHandle = window.CSS && window.CSS.escape ? window.CSS.escape(handle) : handle.replace(/"/g, '\\"');
        const cards = Array.from(document.querySelectorAll(`[data-product-card][data-product-handle="${escapedHandle}"] [data-swatches-container]`))
          .filter((container) => container.dataset.optionName === optionName);

        cards.forEach((container) => {
          const swatches = container.querySelectorAll('[data-swatch]');
          swatches.forEach((swatch) => {
            const value = this.resolveOptionValue(swatch.dataset.optionValue || swatch.dataset.swatchLabel || '');
            const normalized = this.normalizeLabel(value);
            if (!normalized) return;

            const image = swatch.dataset.swatchImage || '';
            const thumbnail = swatch.dataset.swatchThumbnail || image;
            if (!image && !thumbnail) return;

            map[normalized] = {
              image: image || thumbnail || '',
              thumbnail: thumbnail || image || '',
              alt: swatch.dataset.swatchLabel || value,
              variantId: swatch.dataset.variantId || null,
              component: swatch.dataset.bundleComponent || context.componentHandle || '',
              componentTitle: swatch.dataset.bundleComponentTitle || context.componentTitle || '',
              source: 'card'
            };
          });
        });
      }

      return map;
    }

    async loadComponentImages(context, values) {
      if (!context?.componentHandle || !Array.isArray(values) || !values.length) {
        return {};
      }

      const product = await this.getComponentProduct(context.componentHandle);
      if (!product) return {};

      const colorIndex = this.findColorOptionIndex(product, context);
      if (colorIndex === -1) return {};

      const normalizedTargets = new Set(
        values.map((value) => this.normalizeLabel(value)).filter(Boolean)
      );
      if (!normalizedTargets.size) return {};

      const variants = Array.isArray(product.variants) ? product.variants : [];
      const map = {};

      variants.forEach((variant) => {
        if (!variant) return;
        const variantOptions = Array.isArray(variant.options) ? variant.options : [];
        const optionValue = variantOptions[colorIndex];
        const normalizedValue = this.normalizeLabel(this.resolveOptionValue(optionValue));
        if (!normalizedValue || !normalizedTargets.has(normalizedValue)) return;

        const image = variant.featured_image?.src || variant.image || null;
        if (!image) return;

        map[normalizedValue] = {
          image,
          thumbnail: image,
          alt: variant.featured_image?.alt || variant.title || optionValue || context.componentTitle || '',
          variantId: variant.id || null,
          component: context.componentHandle,
          componentTitle: context.componentTitle || '',
          source: 'component'
        };
      });

      return map;
    }

    findColorOptionIndex(product, context = {}) {
      if (!product || !Array.isArray(product.options)) return -1;

      const attributeNormalized = context.attribute ? this.normalizeLabel(context.attribute) : '';

      for (let index = 0; index < product.options.length; index += 1) {
        const option = product.options[index];
        const optionName = typeof option === 'string' ? option : option?.name;
        if (!optionName) continue;

        const normalized = this.normalizeLabel(optionName);
        if (attributeNormalized && normalized === attributeNormalized) {
          return index;
        }

        if (this.determineOptionType(optionName) === 'color') {
          return index;
        }
      }

      return -1;
    }

    async getComponentProduct(handle) {
      if (!handle) return null;
      const normalizedHandle = handle.toString().trim();
      if (!normalizedHandle) return null;

      if (this.componentProductCache.has(normalizedHandle)) {
        return this.componentProductCache.get(normalizedHandle);
      }

      const fetchPromise = fetch(`/products/${normalizedHandle}.js`)
        .then((response) => {
          if (!response.ok) return null;
          return response.json();
        })
        .then((product) => {
          if (product) {
            this.componentProductData.set(normalizedHandle, product);
          }
          return product;
        })
        .catch(() => null);

      this.componentProductCache.set(normalizedHandle, fetchPromise);
      return fetchPromise;
    }

    getComponentImageFromCache(handle, value, context = {}) {
      if (!handle) return null;
      const normalizedHandle = handle.toString().trim();
      if (!normalizedHandle) return null;

      const product = this.componentProductData.get(normalizedHandle);
      if (!product || !Array.isArray(product.variants)) return null;

      const colorIndex = this.findColorOptionIndex(product, context);
      if (colorIndex === -1) return null;

      const normalizedTarget = this.normalizeLabel(this.resolveOptionValue(value));
      if (!normalizedTarget) return null;

      for (const variant of product.variants) {
        if (!variant) continue;
        const options = Array.isArray(variant.options) ? variant.options : [];
        const optionValue = options[colorIndex];
        const normalizedValue = this.normalizeLabel(this.resolveOptionValue(optionValue));
        if (!normalizedValue || normalizedValue !== normalizedTarget) continue;

        const image = variant.featured_image?.src || variant.image || null;
        if (image) {
          return {
            image,
            thumbnail: image,
            alt: variant.featured_image?.alt || variant.title || optionValue || context.componentTitle || '',
            variantId: variant.id || null,
            component: handle,
            componentTitle: context.componentTitle || '',
            source: 'component-cache'
          };
        }
      }

      return null;
    }

    enrichWizardContainer(container) {
      if (!container) return;

      const store = window.BeezfySwatchDataStore;
      const optionName = container.dataset.optionName;
      const optionKey = this.normalizeLabel(optionName);
      const map = optionKey ? this.bundleColorImages[optionKey] : null;

      if (store && typeof store.enrichContainer === 'function') {
        try {
          store.enrichContainer(container);
        } catch (error) {
          console.warn('[BeezfyVariantWizard] enrichContainer failed', error);
        }
      }

      if (!map) return;

      const applyMedia = store?.applyImageToSwatch?.bind(store);
      container.querySelectorAll('[data-swatch]').forEach((swatch) => {
        const value = this.resolveOptionValue(swatch.dataset.optionValue || swatch.dataset.swatchLabel || '');
        const normalizedValue = this.normalizeLabel(value);
        if (!normalizedValue) return;
        const media = map[normalizedValue];
        if (!media || !media.image) return;

        if (typeof applyMedia === 'function') {
          try {
            applyMedia(swatch, media);
          } catch (error) {
            console.warn('[BeezfyVariantWizard] applyImageToSwatch failed', error);
            this.applyImageFallback(swatch, media);
          }
        } else {
          this.applyImageFallback(swatch, media);
        }
      });
    }

    applyImageFallback(swatch, media) {
      if (!swatch || !media?.image) return;
      let img = swatch.querySelector('img');
      if (!img) {
        img = document.createElement('img');
        img.className = 'beezfy-swatch__image';
        img.loading = 'lazy';
        swatch.innerHTML = '';
        swatch.appendChild(img);
      }
      img.src = media.image;
      img.alt = media.alt || swatch.dataset.swatchLabel || '';
      swatch.dataset.swatchImage = media.image;
      if (media.thumbnail) {
        swatch.dataset.swatchThumbnail = media.thumbnail;
      }
    }

    handleize(value) {
      if (value === undefined || value === null) return '';
      return value
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    normalizeLabel(label) {
      if (!label) return '';
      return label
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    }

    escapeAttribute(value) {
      if (value === undefined || value === null) return '';
      return value
        .toString()
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }

    resolveOptionValue(value) {
      if (value === undefined || value === null) return '';
      if (typeof value === 'string') return value.trim();
      if (typeof value === 'number') return String(value);
      if (typeof value === 'object') {
        const resolved = value.value || value.label || value.name || value.title || value.text || '';
        return typeof resolved === 'string' ? resolved.trim() : String(resolved);
      }
      return String(value);
    }

    extractOptionTokens(optionName) {
      const normalized = this.normalizeLabel(optionName);
      if (!normalized) return [];
      return normalized.split(/\s+/).filter(Boolean);
    }

    getAliasSet(type) {
      this.aliasCache[type] = this.aliasCache[type] || new Set();
      const set = this.aliasCache[type];
      if (set.size) return set;

      const addAlias = (source) => {
        if (!source) return;
        const process = (value) => {
          if (value === undefined || value === null) return;
          const normalized = this.normalizeLabel(value);
          if (!normalized) return;
          set.add(normalized);
          normalized.split(' ').forEach((part) => {
            if (part) set.add(part);
          });
        };

        if (Array.isArray(source)) {
          source.forEach(process);
        } else if (typeof source === 'string') {
          source.split(',').forEach(item => process(item));
        }
      };

      const store = window.BeezfySwatchDataStore;
      const theme = window.BeezfyTheme?.settings || {};

      switch (type) {
        case 'colorOptions':
          addAlias(this.settings.wizard_color_options || this.settings.swatch_color_options);
          addAlias(theme.swatch_color_option_names);
          if (store?.colorOptionNames) addAlias(store.colorOptionNames);
          break;
        case 'colorAttributes':
          addAlias(this.settings.wizard_color_attributes || this.settings.beezfy_swatch_color_attribute_names || this.settings.swatch_color_attribute_names);
          addAlias(theme.beezfy_swatch_color_attribute_names || theme.swatch_color_attribute_names);
          if (store?.colorAttributeNames) addAlias(store.colorAttributeNames);
          break;
        case 'sizeOptions':
          addAlias(this.settings.wizard_size_options || this.settings.swatch_size_option_names);
          addAlias(theme.swatch_size_option_names);
          if (store?.sizeOptionNames) addAlias(store.sizeOptionNames);
          break;
        case 'sizeAttributes':
          addAlias(this.settings.wizard_size_attributes || this.settings.beezfy_swatch_size_attribute_names || this.settings.swatch_size_attribute_names);
          addAlias(theme.beezfy_swatch_size_attribute_names || theme.swatch_size_attribute_names);
          if (store?.sizeAttributeNames) addAlias(store.sizeAttributeNames);
          break;
        case 'imageOptions':
          addAlias(this.settings.wizard_image_options || this.settings.swatch_image_options);
          break;
      }

      // Defaults
      if (!set.size) {
        const defaults = {
          colorOptions: 'color,colour,couleur,colore,farbe,colores,colores,색상',
          colorAttributes: 'stile,style,shade,tonalità,tonalité,tone,nuance,teint,tinta',
          sizeOptions: 'size,taglia,taille,tamaño,größe,maat,dimension',
          sizeAttributes: 'size,taglia,taille,tamaño,dimension,maß,mas',
          imageOptions: 'style,pattern,design,print,graphic'
        };
        if (defaults[type]) addAlias(defaults[type]);
      }

      return set;
    }

    determineOptionType(optionName) {
      const context = this.extractBundleOptionContext(optionName);
      const attributeToken = context.attribute ? this.normalizeLabel(context.attribute) : '';
      const tokens = this.extractOptionTokens(optionName);

      const colorOptionTokens = this.getAliasSet('colorOptions');
      const colorAttributeTokens = this.getAliasSet('colorAttributes');
      const sizeOptionTokens = this.getAliasSet('sizeOptions');
      const sizeAttributeTokens = this.getAliasSet('sizeAttributes');
      const imageOptionTokens = this.getAliasSet('imageOptions');

      const attrColor = attributeToken && colorAttributeTokens.has(attributeToken);
      const attrSize = attributeToken && sizeAttributeTokens.has(attributeToken);

      const tokenMatches = (set) => tokens.some(token => set.has(token));

      const colorMatch = attrColor || tokenMatches(colorOptionTokens);
      const sizeMatch = attrSize || tokenMatches(sizeOptionTokens);
      const imageMatch = tokenMatches(imageOptionTokens);

      if (attrColor && !attrSize) return 'color';
      if (attrSize && !attrColor) return 'size';
      if (imageMatch && !colorMatch && !sizeMatch) return 'image';
      if (colorMatch && !sizeMatch) return 'color';
      if (sizeMatch && !colorMatch) return 'size';
      if (imageMatch && colorMatch && !sizeMatch) return 'color';
      if (imageMatch) return 'image';
      return 'text';
    }

    getSwatchImage(optionName, value) {
      const normalizedOption = this.normalizeLabel(optionName);
      const normalizedValue = this.normalizeLabel(value);

      if (normalizedOption && normalizedValue && this.bundleColorImages[normalizedOption]?.[normalizedValue]) {
        const bundleMedia = this.bundleColorImages[normalizedOption][normalizedValue];
        if (bundleMedia?.thumbnail || bundleMedia?.image) {
          return bundleMedia.thumbnail || bundleMedia.image;
        }
      }

      // Check for custom swatch images in settings
      const customImages = window.BeezfyTheme?.swatches?.images || {};
      const lowerValue = typeof value === 'string' ? value.toLowerCase() : '';
      if (lowerValue && customImages[lowerValue]) {
        return customImages[lowerValue];
      }

      const variantImage = this.getVariantImage(optionName, value);
      if (variantImage) {
        return variantImage;
      }

      // Try to find product variant image as final fallback
      const variant = lowerValue && Array.isArray(this.product?.variants)
        ? this.product.variants.find(v =>
            Array.isArray(v?.options) && v.options.some(opt => opt && opt.toLowerCase() === lowerValue)
          )
        : null;

      return variant?.featured_image?.src || variant?.featured_image || null;
    }

    formatMoney(cents) {
      const money = (cents / 100).toFixed(2);
      return window.Shopify?.currency?.active ?
        `${window.Shopify.currency.active} ${money}` :
        `$${money}`;
    }

    /**
     * Check if an option value is available in wizard given previous selections
     * @param {number} optionIndex - Index of the option to check (0, 1, or 2)
     * @param {string} optionValue - Value to check availability for
     * @returns {boolean} - True if there's at least one available variant with this combination
     */
    isOptionValueAvailableInWizard(optionIndex, optionValue) {
      if (!this.product || !this.product.variants) return true;

      // Get selections from previous steps (not current or future steps)
      const previousSelections = {};
      for (let i = 0; i < optionIndex; i++) {
        const option = this.product.options[i];
        const optionName = typeof option === 'string' ? option : option?.name;
        const selectedValue = this.selections[optionName];
        if (selectedValue) {
          previousSelections[i] = selectedValue;
        }
      }

      // Check if ANY variant matches this combination
      return this.product.variants.some(variant => {
        if (!variant.available) return false;

        // Must match the option value we're checking
        if (variant.options[optionIndex] !== optionValue) return false;

        // Must match all previous selections
        for (const [index, value] of Object.entries(previousSelections)) {
          if (variant.options[parseInt(index)] !== value) return false;
        }

        return true;
      });
    }

    async preloadBundleMetadata(product) {
      if (!product || !window.BeezfySwatchDataStore) return;

      try {

        // Check if product is a bundle
        const isBundle = product.tags?.some(tag =>
          tag.toLowerCase().includes('bundle') ||
          tag.toLowerCase().includes('kit')
        ) || product.type?.toLowerCase().includes('bundle');

        if (!isBundle) return;

        // Extract bundle components from product metafields or tags
        const bundleProducts = [];

        // Try to get bundle products from metafields
        if (product.metafields?.bundle?.products) {
          bundleProducts.push(...product.metafields.bundle.products);
        }

        // Try to extract from option names
        product.options?.forEach(option => {
          const optionName = typeof option === 'string' ? option : option?.name;
          if (!optionName) return;

          const context = this.extractBundleOptionContext(optionName);
          if (context.componentHandle) {
            bundleProducts.push(context.componentHandle);
          }
        });

        // Preload component products
        if (bundleProducts.length) {
          await Promise.all(
            bundleProducts.map(handle =>
              this.getComponentProduct(handle)
            )
          );
        }
      } catch (error) {
        console.warn('[BeezfyVariantWizard] Failed to preload bundle metadata', error);
      }
    }

    async loadBundleDataFromCards() {
      if (!this.product?.handle) return;

      try {
        // Find product cards with matching handle
        const escapedHandle = window.CSS?.escape ?
          window.CSS.escape(this.product.handle) :
          this.product.handle.replace(/"/g, '\\"');

        const cards = document.querySelectorAll(
          `[data-product-card][data-product-handle="${escapedHandle}"]`
        );

        if (!cards.length) return;

        cards.forEach(card => {
          // Extract bundle data from card swatches
          const swatchContainers = card.querySelectorAll('[data-swatches-container]');

          swatchContainers.forEach(container => {
            const optionName = container.dataset.optionName;
            if (!optionName) return;

            const normalizedKey = this.normalizeLabel(optionName);
            if (!this.bundleColorImages[normalizedKey]) {
              this.bundleColorImages[normalizedKey] = {};
            }

            const swatches = container.querySelectorAll('[data-swatch]');
            swatches.forEach(swatch => {
              const value = swatch.dataset.optionValue || swatch.dataset.swatchLabel;
              if (!value) return;

              const normalizedValue = this.normalizeLabel(value);
              const swatchImage = swatch.dataset.swatchImage;
              const bundleComponent = swatch.dataset.bundleComponent;
              const bundleComponentTitle = swatch.dataset.bundleComponentTitle;

              if (swatchImage && !this.bundleColorImages[normalizedKey][normalizedValue]) {
                this.bundleColorImages[normalizedKey][normalizedValue] = {
                  image: swatchImage,
                  thumbnail: swatch.dataset.swatchThumbnail || swatchImage,
                  alt: swatch.dataset.swatchLabel || value,
                  variantId: swatch.dataset.variantId || null,
                  component: bundleComponent || '',
                  componentTitle: bundleComponentTitle || '',
                  source: 'card'
                };
              }
            });
          });
        });

        // Also check for bundle metadata in page
        const variantSelector = document.querySelector('[data-variant-selector]');
        if (variantSelector && variantSelector.dataset.productHandle === this.product.handle) {
          const containers = variantSelector.querySelectorAll('[data-swatches-container]');

          containers.forEach(container => {
            const optionName = container.dataset.optionName;
            if (!optionName) return;

            const normalizedKey = this.normalizeLabel(optionName);
            if (!this.bundleColorImages[normalizedKey]) {
              this.bundleColorImages[normalizedKey] = {};
            }

            const isBundleOption = container.dataset.isBundleOption === 'true' ||
                                  container.dataset.isBundle === 'true';

            if (isBundleOption) {
              const bundleComponent = container.dataset.bundleComponent;
              const bundleComponentTitle = container.dataset.bundleComponentTitle;

              // Store bundle context for this option
              const context = this.getBundleOptionContext(optionName);
              context.isBundleOption = true;
              context.componentHandle = bundleComponent || context.componentHandle;
              context.componentTitle = bundleComponentTitle || context.componentTitle;

              const normalized = this.normalizeLabel(optionName);
              this.bundleOptionMetadata[normalized] = context;
            }
          });
        }
      } catch (error) {
        console.warn('[BeezfyVariantWizard] Failed to load bundle data from cards', error);
      }
    }
  }

  // Initialize when DOM is ready and expose globally
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.BeezfyVariantWizard = new BeezfyVariantWizard();
    });
  } else {
    window.BeezfyVariantWizard = new BeezfyVariantWizard();
  }

})();
